import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Eye, MessageCircle, ShoppingBag, TrendingUp, ExternalLink, RefreshCw } from "lucide-react";

const PERIODOS = [
  { key: "hoy", label: "Hoy" },
  { key: "semana", label: "Esta semana" },
  { key: "mes", label: "Este mes" },
];

export default function LandingAnalytics() {
  const [data, setData] = useState(null);
  const [periodo, setPeriodo] = useState("hoy");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/landing?periodo=${periodo}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [periodo]);

  return (
    <div className="space-y-6" data-testid="landing-analytics-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Analítica Landing</h1>
          <p className="text-sm text-zinc-500">Visitas, clics y conversiones de casacompleta.com</p>
        </div>
        <div className="flex gap-2">
          {PERIODOS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriodo(p.key)}
              className={`px-4 h-9 text-xs font-bold uppercase tracking-widest border-2 ${periodo === p.key ? "bg-black text-[#FFD100] border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`}
              data-testid={`periodo-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <button onClick={load} className="p-2 border-2 border-zinc-300 rounded hover:border-black" data-testid="refresh-analytics"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={<Eye />} label="Visitas" value={data?.visitas ?? "—"} color="bg-blue-50 text-blue-900 border-blue-200" />
        <KPI icon={<MessageCircle />} label="Clics WhatsApp" value={data?.whatsapp_clicks ?? "—"} color="bg-green-50 text-green-900 border-green-200" />
        <KPI icon={<ShoppingBag />} label="Al carrito" value={data?.add_to_cart ?? "—"} color="bg-yellow-50 text-yellow-900 border-yellow-200" />
        <KPI icon={<TrendingUp />} label="Pedidos" value={data?.checkouts ?? "—"} color="bg-red-50 text-red-900 border-red-200" />
        <KPI icon={<TrendingUp />} label="Ventas Q" value={formatMoney(data?.ventas_generadas || 0)} color="bg-emerald-50 text-emerald-900 border-emerald-200" big />
      </div>

      {/* Conversión */}
      <Card className="p-5">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Tasa de conversión</div>
        <div className="text-4xl md:text-5xl font-black text-[#002FA7] mt-1">{data?.conversion_rate ?? 0}%</div>
        <div className="text-xs text-zinc-500 mt-1">Pedidos por WhatsApp sobre visitas totales</div>
      </Card>

      {/* Por fuente */}
      <Card className="p-5">
        <h2 className="font-semibold text-lg mb-3">Origen del tráfico</h2>
        {!data || data.por_fuente.length === 0 ? (
          <p className="text-sm text-zinc-500">Aún no hay datos. Los eventos se registran cuando los clientes visitan la tienda con parámetros UTM.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest">Fuente</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-right">Visitas</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-right">Pedidos</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-right">Ventas</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.por_fuente.map((f) => (
                <tr key={f.fuente}>
                  <td className="px-3 py-2 font-medium capitalize">{f.fuente}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{f.visitas}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{f.conversiones}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{formatMoney(f.ventas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Por campaña */}
      <Card className="p-5">
        <h2 className="font-semibold text-lg mb-3">Por campaña</h2>
        {!data || data.por_campana.length === 0 ? (
          <p className="text-sm text-zinc-500">Aún no hay datos de campañas. Usa URLs con `?utm_campaign=nombre_campana` en tus anuncios para trackearlas.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest">Campaña</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-right">Visitas</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-right">Conversiones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.por_campana.map((c) => (
                <tr key={c.campana}>
                  <td className="px-3 py-2 font-medium">{c.campana}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.visitas}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.conversiones}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-5 bg-blue-50 border-blue-200">
        <h3 className="font-semibold mb-2">📊 Cómo trackear campañas</h3>
        <p className="text-sm text-zinc-700 mb-2">Cuando pautes en Google/Facebook/Instagram, usa URLs con parámetros UTM. Ejemplo:</p>
        <code className="block text-xs bg-white p-2 rounded border border-blue-200 break-all">
          https://order-hub-627.emergent.host/?utm_source=facebook&utm_medium=cpc&utm_campaign=dia_de_la_madre
        </code>
        <p className="text-xs text-zinc-600 mt-2">Cada visita con esos parámetros aparecerá en las tablas de "Origen" y "Campaña".</p>
      </Card>
    </div>
  );
}

function KPI({ icon, label, value, color, big }) {
  return (
    <Card className={`p-4 border-2 ${color || ""}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-4 h-4">{icon}</div>
        <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
      </div>
      <div className={`${big ? "text-2xl" : "text-3xl"} font-black tabular-nums`}>{value}</div>
    </Card>
  );
}
